// /app/inbox — Workspace-wide approvals queue with split list + preview.
//
// Per design/Handoff §01 + 2026-05-22 restyle from handoff:
//   Left:  list of pending items (approvals / comments / telegram tabs)
//   Right: preview pane for the selected approval — variants comparison +
//          schedule action.
//
// Backend wiring (PRESERVED):
//   GET /v1/approvals?limit=100 — content-service v0.2.64
//   POST /v1/versions/:id/accept · /reject
//   POST /v1/generations/:id/regenerate
//
// Comments + Telegram are stubbed with empty states pending comments-service
// (Session 10+) and telegram inbound webhook (Session 11+).

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/lib/asset-url";
import { useBrands } from "@/hooks/use-persona";
import { Icons } from "@/components/icons";
import {
  Badge,
  BrandMark,
  Empty,
  PageHead,
  PlatformChip,
  Tabs,
  ImageFailureNotice,
  imageFailure,
} from "@/components/primitives";
import type { Brand, Platform } from "@/lib/types";

export const Route = createFileRoute("/app/inbox")({
  component: InboxPage,
});

interface Approval {
  id: string;
  generation_id: string;
  brand_id: string;
  platform: Platform;
  provider?: string;
  status: string;
  text_body?: string | null;
  hashtags?: string[];
  image_url?: string | null;
  created_at: string;
  prompt?: string;
  /** Additive — why the image leg failed. Absent on older rows. */
  image_error?: string | null;
  image_error_kind?: string | null;
}

type Tab = "approvals" | "comments" | "telegram";

const PLATFORM_LABEL: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  x: "X",
  linkedin: "LinkedIn",
  telegram: "Telegram",
};

function InboxPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";

  const [tab, setTab] = useState<Tab>("approvals");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: brandsData } = useBrands();
  const brands = brandsData?.data ?? [];
  const brandMap = new Map(brands.map((b) => [b.id, b]));

  const qc = useQueryClient();
  const queryKey = ["approvals"];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      api<{ approvals: Approval[]; total: number }>(`/approvals?limit=100`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const approvals = data?.approvals ?? [];
  const selected =
    approvals.find((a) => a.id === selectedId) ?? approvals[0] ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "07 · Gelen kutusu" : "07 · Inbox"}
        title={tr ? "Onaylar, yanıtlar, incelemeler." : "Your approvals, replies, and reviews."}
        subtitle={
          tr
            ? "İki akış — onay bekleyen varyantlar ve müşteri yorumları. Buradan onaylayabilir ya da Telegram'dan da yapabilirsin."
            : "Two streams — variants needing approval and customer comments. Approve from here or via Telegram."
        }
      />

      <div style={{ padding: "0 32px", borderBottom: "1px solid var(--b1)" }}>
        <Tabs
          value={tab}
          onChange={(v) => setTab(v as Tab)}
          tabs={[
            {
              id: "approvals",
              label: tr ? "Onaylar" : "Approvals",
              count: approvals.length,
              icon: <Icons.Check size={13} />,
            },
            {
              id: "comments",
              label: tr ? "Yorumlar" : "Comments",
              icon: <Icons.MessageCircle size={13} />,
            },
            {
              id: "telegram",
              label: tr ? "Telegram üzerinden" : "Via Telegram",
              icon: <Icons.Send size={13} />,
            },
          ]}
        />
      </div>

      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "380px 1fr",
          overflow: "hidden",
        }}
      >
        {/* Left list */}
        <div style={{ borderRight: "1px solid var(--b1)", overflow: "auto" }}>
          {tab === "approvals" ? (
            isLoading ? (
              <ListSkeleton />
            ) : approvals.length === 0 ? (
              <div style={{ padding: 32 }}>
                <Empty
                  title={tr ? "Onay kuyruğu boş" : "Inbox is empty"}
                  body={
                    tr
                      ? "Üret sayfasından yeni içerik oluşturduğunda burada belirir."
                      : "New variants from Generate will appear here."
                  }
                />
              </div>
            ) : (
              approvals.map((a) => {
                const b = brandMap.get(a.brand_id);
                const active = a.id === (selected?.id ?? selectedId);
                const accent = b?.accent_hex || "var(--accent)";
                const accentSoft = b?.accent_soft_hex || "var(--accent-soft)";
                return (
                  <button
                    key={a.id}
                    onClick={() => setSelectedId(a.id)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "14px 18px",
                      borderBottom: "1px solid var(--b1)",
                      background: active
                        ? `linear-gradient(90deg, ${accentSoft}, transparent)`
                        : "transparent",
                      position: "relative",
                    }}
                  >
                    {active ? (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          background: accent,
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 8,
                      }}
                    >
                      {b ? <BrandMark brand={b} size={20} /> : null}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "var(--tx)",
                        }}
                      >
                        {b?.trade_name ?? a.brand_id.slice(0, 8)}
                      </span>
                      <PlatformChip platform={a.platform} size={16} />
                      <div style={{ flex: 1 }} />
                      <Badge
                        status="pending"
                        label={tr ? "bekliyor" : "pending"}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--tx)",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical" as never,
                        overflow: "hidden",
                      }}
                    >
                      {a.text_body || (tr ? "(metin yok)" : "(no text)")}
                    </div>
                    <div
                      className="mono"
                      style={{ fontSize: 10, color: "var(--mu)", marginTop: 6 }}
                    >
                      {formatRel(a.created_at, tr)}
                    </div>
                  </button>
                );
              })
            )
          ) : null}

          {tab === "comments" ? (
            <div style={{ padding: 32 }}>
              <Empty
                icon={<Icons.MessageCircle size={42} style={{ color: "var(--mu)" }} />}
                title={tr ? "Yorumlar yakında" : "Comments coming soon"}
                body={
                  tr
                    ? "Müşteri yorumları comments-service hazır olduğunda burada listelenecek."
                    : "Customer comments will list here once comments-service ships."
                }
              />
            </div>
          ) : null}

          {tab === "telegram" ? (
            <div style={{ padding: 32 }}>
              <Empty
                icon={
                  <Icons.Send size={42} style={{ color: "var(--telegram, #26A5E4)" }} />
                }
                title={tr ? "Telefonundan onayla" : "Approve from your phone"}
                body={
                  tr
                    ? "Telegram'ı bağla — yeni varyantlar onayla/reddet butonlarıyla mesaj olarak gelir. Buradaki aksiyonlarla otomatik eşleşir."
                    : "Connect Telegram to receive new variants as messages with approve/reject buttons."
                }
                action={
                  <button className="btn btn-primary btn-sm">
                    {tr ? "Telegram'ı bağla" : "Connect Telegram"}
                  </button>
                }
              />
            </div>
          ) : null}
        </div>

        {/* Right preview */}
        <div
          className="scroll-y"
          style={{ overflow: "auto", padding: 28, background: "var(--bg)" }}
        >
          {tab === "approvals" && selected ? (
            <PreviewPane
              approval={selected}
              brand={brandMap.get(selected.brand_id)}
              tr={tr}
              onAfterAction={() => {
                qc.invalidateQueries({ queryKey });
                setSelectedId(null);
              }}
            />
          ) : (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--mu)",
              }}
            >
              {tab === "approvals"
                ? tr
                  ? "Önizlemek için bir öğe seç"
                  : "Pick an item to preview"
                : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Right preview pane ────────────────────────────────────────────────────

function PreviewPane({
  approval,
  brand,
  tr,
  onAfterAction,
}: {
  approval: Approval;
  brand: Brand | undefined;
  tr: boolean;
  onAfterAction: () => void;
}) {
  const accept = useMutation({
    mutationFn: () => api(`/versions/${approval.id}/accept`, { method: "POST" }),
    onSuccess: onAfterAction,
  });
  const reject = useMutation({
    mutationFn: () => api(`/versions/${approval.id}/reject`, { method: "POST" }),
    onSuccess: onAfterAction,
  });
  const regen = useMutation({
    // Backend requires { platform, scope } — sending no body returned
    // 400 "bad json: EOF", so the button silently did nothing. scope:"both"
    // regenerates a fresh caption + image variant.
    mutationFn: () =>
      api(`/generations/${approval.generation_id}/regenerate`, {
        method: "POST",
        body: { platform: approval.platform, scope: "both" },
      }),
    onSuccess: onAfterAction,
  });

  const accent = brand?.accent_hex || "var(--accent)";
  const platformLabel = PLATFORM_LABEL[approval.platform];

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        — {tr ? "Onay bekleyen" : "Pending approval"}
      </div>
      <h2 style={{ fontSize: 22, marginBottom: 18 }}>
        {brand?.trade_name ?? approval.brand_id.slice(0, 8)} · {platformLabel}
      </h2>

      <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
        {approval.image_url ? (
          <img
            src={resolveImageUrl(approval.image_url)}
            alt=""
            style={{
              width: "100%",
              maxHeight: 560,
              objectFit: "contain",
              background: "#0a0a0a",
              borderBottom: "1px solid var(--b1)",
              display: "block",
            }}
          />
        ) : (
          <div
            style={{
              aspectRatio:
                approval.platform === "x"
                  ? "16/9"
                  : approval.platform === "instagram"
                    ? "4/5"
                    : approval.platform === "facebook" ||
                        approval.platform === "linkedin"
                      ? "1.91/1"
                      : "1",
              background: `linear-gradient(135deg, ${accent}40, #000 50%, ${accent}20)`,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderBottom: "1px solid var(--b1)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--display)",
                fontWeight: 800,
                fontSize: 36,
                color: "#fff",
                letterSpacing: "-0.02em",
              }}
            >
              {brand?.trade_name?.split(" ")[0] ?? "—"}
            </div>
            {/* Explains a missing image when the backend said why. Renders
                nothing when it didn't — the brand-mark placeholder above is
                then all you see, exactly as before. */}
            <ImageFailureNotice failure={imageFailure(approval)} tr={tr} overlay />
          </div>
        )}
        <div style={{ padding: 18 }}>
          {approval.text_body ? (
            <p
              style={{
                fontSize: 13.5,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                color: "var(--tx)",
              }}
            >
              {approval.text_body}
            </p>
          ) : (
            <p style={{ color: "var(--mu)", fontSize: 13, fontStyle: "italic" }}>
              {tr ? "(metin yok)" : "(no text)"}
            </p>
          )}
          {approval.hashtags && approval.hashtags.length > 0 ? (
            <div
              style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}
            >
              {approval.hashtags.map((h) => (
                <span key={h} style={{ fontSize: 12, color: accent }}>
                  #{h}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div
          style={{
            borderTop: "1px solid var(--b1)",
            padding: "10px 14px",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => reject.mutate()}
            disabled={reject.isPending}
            title={tr ? "Reddet" : "Reject"}
          >
            <Icons.X size={13} /> {tr ? "Reddet" : "Reject"}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => regen.mutate()}
            disabled={regen.isPending}
            title={tr ? "Yeniden üret" : "Regenerate"}
          >
            <Icons.Refresh size={13} /> {tr ? "Yeniden" : "Regenerate"}
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => accept.mutate()}
            disabled={accept.isPending}
          >
            <Icons.Check size={13} /> {tr ? "Onayla & Planla" : "Approve & schedule"}
          </button>
        </div>
      </div>

      <div
        className="card"
        style={{
          padding: 18,
          marginTop: 8,
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <Icons.Clock size={16} style={{ color: "var(--warning)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {tr ? "Zaman çizelgesi" : "Schedule"}
          </div>
          <div
            className="mono"
            style={{ fontSize: 11, color: "var(--tx2)", marginTop: 2 }}
          >
            {tr
              ? "Onaylayınca gönderi, kampanya tarih aralığındaki en iyi saate otomatik planlanır. Yeniden, takvimden değiştirebilirsin."
              : "On approve, the post is auto-scheduled at the best time in your campaign window. You can still adjust it from the calendar."}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Skeleton + helpers ────────────────────────────────────────────────────

function ListSkeleton() {
  return (
    <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skel" style={{ height: 88, borderRadius: 8 }} />
      ))}
    </div>
  );
}

function formatRel(iso: string, tr: boolean): string {
  const t = new Date(iso).getTime();
  const m = (Date.now() - t) / 60_000;
  if (m < 1) return tr ? "şimdi" : "now";
  if (m < 60)
    return tr ? `${Math.round(m)} dk önce` : `${Math.round(m)} min ago`;
  if (m < 60 * 24)
    return tr ? `${Math.round(m / 60)} sa önce` : `${Math.round(m / 60)} h ago`;
  return tr
    ? `${Math.round(m / (60 * 24))} gün önce`
    : `${Math.round(m / (60 * 24))} d ago`;
}
